import type {ReactNode} from 'react';
import React, {useCallback, useState} from 'react';

import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';

import AnnotationPopover from './AnnotationPopover';
import {useAnnotationModeStore} from './AnnotationModeStore';
import {selectAnnotationType, selectHighlightColor} from './annotationSelectors';
import type {AnnotationAnchor} from './annotationTypes';
import {useAnnotations} from './useAnnotations';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

interface AnnotatableContentProps {
  questionId: string;
  children: ReactNode;
}

/**
 * Wraps children so selections inside them become annotations (see
 * `useAnnotations`). Offsets index the rendered visible text, not the
 * source markdown — future persistence must account for that.
 */
export default function AnnotatableContent({questionId, children}: AnnotatableContentProps) {
  const {isFeatureEnabled, isLoading} = useIsFeatureFlagEnabled('student_annotations');
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const {captureAnnotation, getAnnotationAtPoint, containerRef} = useAnnotations(questionId);
  const setHighlightColor = useAnnotationModeStore((state) => state.setHighlightColor);
  const setAnnotationUnderline = useAnnotationModeStore((state) => state.setAnnotationUnderline);
  const removeAnnotation = useAnnotationModeStore((state) => state.removeAnnotation);
  const [activeAnnotation, setActiveAnnotation] = useState<AnnotationAnchor | null>(null);
  // The active annotation's own color and type drive the popover's selection.
  const activeColor = useAnnotationModeStore((state) =>
    selectHighlightColor(state, questionId, activeAnnotation?.id)
  );
  const activeType = useAnnotationModeStore((state) =>
    selectAnnotationType(state, questionId, activeAnnotation?.id)
  );

  const handleMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const captured = isAnnotationModeOn ? captureAnnotation() : null;
      if (captured) {
        setActiveAnnotation(captured);
        return;
      }
      // Don't hijack a real text selection (e.g. select-to-copy).
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) {
        return;
      }
      // A plain click on an existing annotation opens its color editor (either mode).
      setActiveAnnotation(getAnnotationAtPoint(event.clientX, event.clientY));
    },
    [isAnnotationModeOn, captureAnnotation, getAnnotationAtPoint]
  );

  if (isLoading || !isFeatureEnabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={isAnnotationModeOn ? 'cursor-highlighter' : undefined}
        onMouseUp={handleMouseUp}
      >
        {children}
      </div>
      {activeAnnotation && activeColor && activeType && (
        <AnnotationPopover
          anchorRect={activeAnnotation.rect}
          selectedColor={activeColor}
          selectedType={activeType}
          onSelectColor={(color) => setHighlightColor(questionId, activeAnnotation.id, color)}
          onSelectUnderline={() => setAnnotationUnderline(questionId, activeAnnotation.id)}
          onRemove={() => {
            removeAnnotation(questionId, activeAnnotation.id);
            setActiveAnnotation(null);
          }}
          onDismiss={() => setActiveAnnotation(null)}
        />
      )}
    </>
  );
}
